import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import { SearchResultsTab } from '../../../../types/search';
import InfiniteScroll from 'react-infinite-scroller';
import { hasNext } from '../../../../utils/pagination';
import WithTracking from '../../../utils/WithTracking';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import SearchResultsContainer from '../SearchResultsContainer';
import { getCommonCardProps } from '../SearchResults';
import { useSelector } from 'react-redux';
import {
  getSearchResults as getSearchResultsSelector,
  getMindItemLoadingState as getMindItemLoadingStateSelector,
  SearchLoadingStatus,
  getPaginationSelector,
} from '../../../../reducers/search.selectors';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import VideoMuiCard from '../../../cards/MuiCard/VideoMuiCard/VideoMuiCard';
import { MindItemSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import SearchResultsLoader from '../SearchResultsLoader';
import { getVideoMuiCardProps } from './VideoMindItemsTab';
import MindItemMuiCard, {
  MindItemMuiCardProps,
} from '../../../cards/MuiCard/MindItemMuiCard/MindItemMuiCard';
import { SearchResultsTabProps } from './SearchResultsTab';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import SearchResultsTopbarNewUI from '../SearchResultsTopbar';
import { Box } from '@mui/material';
import RelatedTopicsPills, {
  RELATED_TOPICS_INLINE_INDEX,
} from '../../RelatedTopicsPills/RelatedTopicsPills';
import useSearchExperiments from '../../../../utils/useSearchExperiments';

export const getMindItemMuiCardProps = (
  item: MindItemSearchResult,
  useAiFilters?: boolean
): MindItemMuiCardProps => {
  return {
    ...getCommonCardProps(item, useAiFilters),
    numberOfPages: item.pageCount || undefined,
    product: item.populatedProduct,
    mindItemPageNumber: item.bestMatch?.mindItemPageNumber,
  };
};

const MindItemsTab = ({
  searchTerm,
  requestFilters,
  loadMore,
  searchEngineVersion,
  onShowNotification,
  onRelatedTopicSelect,
}: SearchResultsTabProps) => {
  const mindItems = useSelector((state) => getSearchResultsSelector(state, 'mindItems'));
  const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
  const mindItemLoadingState = useSelector(getMindItemLoadingStateSelector);
  const { mainContentContainer } = useContext(MasterLayoutContext);
  const { useAiFilters } = useSearchExperiments();

  return (
    <Tab.Pane
      eventKey={SearchResultsTab.MINDITEMS}
      data-testid='new-ui-mindItem-tab'
    >
      <SearchResultsTopbarNewUI
        total={mindItemsPagination.total}
        searchTerm={searchTerm}
        isLoaded={mindItemLoadingState === SearchLoadingStatus.LOADED}
        requestFilters={requestFilters}
      />
      {mindItemLoadingState === SearchLoadingStatus.INITIAL || mindItemLoadingState === SearchLoadingStatus.NONE ? (
        <SearchResultsLoader />
      ) : (
        <>
          {mindItems.length > 0 ? (
            <InfiniteScroll
              loadMore={loadMore}
              hasMore={
                hasNext(mindItemsPagination)
                && mindItemLoadingState !== SearchLoadingStatus.PAGINATION
              }
              className='search-results__infinite-scroll-box'
              useWindow={false}
              getScrollParent={() => mainContentContainer}
            >
              <SearchResultsContainer>
                {mindItems.map((mindItem, index) => (
                  <React.Fragment key={mindItem.id}>
                    <SingleSearchResultContainer>
                      <WithTracking
                        events={{
                          onClick: trackingEvents.itemOpen,
                          onMiddleClick: trackingEvents.itemOpen,
                          onContextMenu: trackingEvents.itemOpen,
                        }}
                        eventPayload={{
                          other: {
                            minditem_id: mindItem.id,
                            index,
                            searchEngineVersion,
                          },
                        }}
                      >
                        <Box>
                          {mindItem.fileType === 'video' ? (
                            <VideoMuiCard
                              {...getVideoMuiCardProps(mindItem, useAiFilters)}
                              index={index}
                              onShowNotification={onShowNotification}
                            />
                          ) : (
                            <MindItemMuiCard
                              {...getMindItemMuiCardProps(mindItem, useAiFilters)}
                              index={index}
                              onShowNotification={onShowNotification}
                            />
                          )}
                        </Box>
                      </WithTracking>
                    </SingleSearchResultContainer>
                    {index === RELATED_TOPICS_INLINE_INDEX && onRelatedTopicSelect && (
                      <RelatedTopicsPills
                        variant='inline'
                        term={searchTerm}
                        onSelect={onRelatedTopicSelect}
                      />
                    )}
                  </React.Fragment>
                ))}
              </SearchResultsContainer>
            </InfiniteScroll>
          ) : (
            <SearchResultsContainer>
              <EmptySearchResultsNewUI searchTerm={searchTerm} />
            </SearchResultsContainer>
          )}
          {mindItemLoadingState === SearchLoadingStatus.PAGINATION && (
            <SearchResultsLoader />
          )}
        </>
      )}
    </Tab.Pane>
  );
};

export default MindItemsTab;
